<template>
    <div :class="['page page--light', { 'page--embed': partnerAppEmbedCustomization }]">
        <section class="row gradient-background">
            <div class="wrapper wrapper--no-max-width">
                <header :class="['row__header', 'row__header--margin-bottom', { 'row__header--partner-app': isPartnerApp }]">
                    <back-button :isAbsolute="false"
                        :isBlack="true"
                    />

                    <h1>{{ $t('marketplace.trading.title') }}</h1>
                </header>

                <div class="wrapper__content">
                    <div class="trading__header">
                        <input v-if="!isPartnerApp"
                            id="input-search"
                            v-model="search"
                            type="text"
                            :class="['trading__search', { 'trading__search--active': search }]"
                            :placeholder="$t('marketplace.trading.search')"
                            @keyup="updateSearch()"
                        >

                        <router-link to="/trading-history"
                            class="generic-link trading__header-link"
                        >
                            {{ $t('marketplace.orderHistory.title') }}
                        </router-link>
                    </div>

                    <div v-if="!loading"
                        class="trading__table"
                    >
                        <div v-if="!Object.keys(filteredTokens).length"
                            class="trading__no-results"
                        >
                            {{ $t('marketplace.trading.noResultsTrading') }}
                        </div>

                        <div v-for="(token, index) in filteredTokens"
                            v-else
                            :id="`row-${index}`"
                            :key="'row'+index"
                            :class="{
                                'trading__table-row': true,
                                'row': true,
                                'trading__table-row--show-graphs': showGraphs,
                            }"
                            @click="goToPartnerPage(token)"
                        >
                            <div v-if="!isUnavailable(token)"
                                v-show="cache.showExchange[token.partner.id]"
                                class="row-overlay"
                                @click.stop="cache.showExchange[token.partner.id] = false"
                            >
                                <button :id="`button-buy-${index}`"
                                    :class="['button', 'button--buy', { 'button--disabled': areButtonsDisabled(token) }]"
                                    @click.stop="goToBuySell(token)"
                                >
                                    {{ $t('marketplace.trading.buyChzToToken', {
                                        tokenName: token.shortCode
                                    }) }}
                                </button>

                                <button v-if="!hasSellHidden"
                                    :id="`button-sell-${index}`"
                                    :class="['button', 'button--sell', { 'button--disabled': areButtonsDisabled(token, 'sell') }]"
                                    @click.stop="goToBuySell(token, 'sell')"
                                >
                                    {{ $t('marketplace.trading.sellTokenToChz', {
                                        tokenName: token.shortCode
                                    }) }}
                                </button>
                            </div>

                            <div class="row-data flex flex--vertical-center flex--horizontal-left">
                                <img v-if="token.partner.partnerLogo"
                                    :src="token.partner.partnerLogo"
                                    class="row-data__logo"
                                >
                                <img v-else
                                    class="row-data__logo row-data__logo--placeholder"
                                >
                            </div>

                            <div class="row-data flex flex--horizontal-center">
                                <label class="row-data__short-name">{{ `${token.partner.shortName}/${defaultCurrency.shortCode}` }}</label>
                                <label class="row-data__token-price">{{ cache.price[token.partner.id] }}</label>
                            </div>

                            <div class="row-data flex flex--horizontal-center">
                                <label :class="['row-data__fluctuation', cache.marketFluctuationModifier[token.partner.id]]">
                                    {{ cache.evolutionPercentage[token.partner.id] }}
                                </label>
                            </div>

                            <div class="row-data flex flex--horizontal-center flex--vertical-center">
                                <img v-if="showGraphs && cache.fluctuationGraph[token.partner.id]"
                                    :src="cache.fluctuationGraph[token.partner.id]"
                                    class="row-data__graph"
                                >
                            </div>

                            <div v-tooltip="getTooltip($t('marketplace.trading.highPrice'))"
                                class="row-data row-data--hidden-sm flex"
                            >
                                <i v-if="cache.highPrice[token.partner.id]"
                                    class="row-data__icon row-data__icon-high icon-high"
                                />

                                <label class="row-data__high-price">{{ cache.highPrice[token.partner.id] }}</label>
                            </div>

                            <div v-tooltip="getTooltip($t('marketplace.trading.lowPrice'))"
                                class="row-data row-data--hidden-sm flex"
                            >
                                <i v-if="cache.lowPrice[token.partner.id]"
                                    class="row-data__icon row-data__icon-low icon-low"
                                />

                                <label class="row-data__low-price">{{ cache.lowPrice[token.partner.id] }}</label>
                            </div>

                            <div class="row-data row-data--hidden-sm flex">
                                <label v-if="cache.tradeVolume[token.partner.id]"
                                    class="row-data__title"
                                >{{ $t('marketplace.trading.volume') }}</label>

                                <label class="row-data__trade-volume">{{ cache.tradeVolume[token.partner.id] }}</label>
                            </div>

                            <div class="row-data row-data--hidden-sm flex">
                                <label v-if="cache.tradeAmount[token.partner.id]"
                                    class="row-data__title"
                                >{{ $t('marketplace.trading.amount') }}</label>

                                <label class="row-data__trade-amount">{{ cache.tradeAmount[token.partner.id] }}</label>
                            </div>

                            <div class="row-data flex flex--vertical-center">
                                <i :id="`button-exchange-${index}`"
                                    :class="[
                                        'row-data__icon',
                                        'icon-exchange-horizontal',
                                        'row-data__icon-exchange',
                                        { 'row-data__icon-exchange--disabled': isUnavailable(token) }
                                    ]"
                                    @click.stop="toggleExchange(token.partner.id)"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
// 3rd Party libs
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Utils
import {
    formatPrice,
} from '@shared/utils/formatter.js';

import {
    isWithinInterval,
} from '@shared/utils/dates';

// Config
import {
    PERMISSION_CODE,
} from '@shared/config/user-permissions';

// Components
import backButton from '@webapp/components/back-button/back-button.vue';

// Socios variables
import socios from '@webapp/socios.json';

// Local Types
import localEpicTypes from '@webapp/epics/types.js';
import localStoreTypes from '@webapp/store/types.js';

export default {
    name: 'marketplace-trading',
    components: {
        backButton,
    },
    data() {
        return {
            showGraphs: false,
            cache: {
                price: {},
                evolutionPercentage: {},
                marketFluctuationModifier: {},
                fluctuationGraph: {},
                highPrice: {},
                lowPrice: {},
                tradeVolume: {},
                tradeAmount: {},
                showExchange: {},
                clickEventFunction: null,
            },
            filteredTokens: {},
            search: '',
            loading: true,
        };
    },
    computed: {
        ...mapGetters([
            'isPartnerApp',
            'voteTokenOfferingsByStatusAndTokenId',
            'isFeatureDenied',
            'defaultCurrency',
            'partnerAppEmbedCustomization',
        ]),

        ...mapGetters('voteTokens', [
            'tokens',
            'tokenPriceByPartnerId',
            'isTokenPurchaseRestricted',
            'isTokenSaleRestricted',
            'filteredTokensForMarketplace',
            'tokensForMarketplace',
        ]),

        ...mapGetters('marketplace', [
            'availableMarketsFormatted',
            'marketByPartnerId',
        ]),

        hasSellHidden() {
            return (this.isFeatureDenied(PERMISSION_CODE.MARKETPLACE_SELL) || this.isPartnerApp);
        },

        sortedTokens() {
            return [...this.tokens].sort((a, b) => a.partner?.shortName.localeCompare(b.partner?.shortName));
        },
    },
    watch: {
        availableMarketsFormatted() {
            this.buildLocalCache();
            this.loading = false;

            this.showAppLoader({
                isVisible: false,
            });
        },
    },
    destroyed() {
        document.getElementById('app').removeEventListener('click', this.clickEventFunction);
    },
    mounted() {
        this.showAppLoader({
            isVisible: true,
            hasOpacity: true,
        });

        this.loadMarkets();
        this.clickEventFunction = () => this.closeExchanges();

        document.getElementById('app').addEventListener('click', this.clickEventFunction);
    },
    methods: {
        ...mapActions({
            loadMarkets: localEpicTypes.GET_AVAILABLE_MARKETS,
            showAppLoader: localStoreTypes.SET_APP_LOADER,
        }),
        buildLocalCache() {
            this.sortedTokens.forEach((token) => {
                this.cache.price[token.partner.id] = this.getTokenPrice(token);
                this.cache.evolutionPercentage[token.partner.id] = this.getEvolutionPercentage(token);
                this.cache.marketFluctuationModifier[token.partner.id] = this.getMarketFluctuationModifier(token);
                this.cache.fluctuationGraph[token.partner.id] = this.getFluctuationGraph(token);
                this.cache.lowPrice[token.partner.id] = this.getLowPrice(token);
                this.cache.highPrice[token.partner.id] = this.getHighPrice(token);
                this.cache.tradeVolume[token.partner.id] = this.getTradeVolume(token);
                this.cache.tradeAmount[token.partner.id] = this.getTradeAmount(token);

                this.$set(this.cache.showExchange, token.partner.id, false);
            });

            this.filteredTokens = this.sortedTokens;
        },
        getTokenPrice(token) {
            // Obtain ticker prices just for marketplace and partner pages
            const market = this.marketByPartnerId(token.partner.id);

            let price = null;

            if (market && market.ticker) {
                price = market.ticker.lastPrice;
            } else {
                const { tokenPrice: { rawValue = '' } = {} } = this.tokenPriceByPartnerId(token.partner, this.defaultCurrency.id);

                if (rawValue) {
                    price = Number(rawValue);
                }
            }

            return price === null ? 'TBA' : formatPrice(price);
        },
        getEvolutionPercentage(token) {
            const market = this.marketByPartnerId(token.partner.id);

            let evolution = this.getFluctuationGraph(token) ? '--' : '';

            if (market.ticker) {
                if (market.ticker.evolutionPercentage > 0) {
                    evolution = `+${market.ticker.evolutionPercentage}%`;
                } else if (parseFloat(market.ticker.evolutionPercentage) !== 0) {
                    evolution = `${market.ticker.evolutionPercentage}%`;
                }
            }

            return evolution;
        },
        getFluctuationGraph(token) {
            const market = this.marketByPartnerId(token.partner.id);

            return market.graphFluctuation ? market.graphFluctuation : '';
        },
        getTradeAmount(token) {
            const market = this.marketByPartnerId(token.partner.id);

            return market.ticker && market.ticker.tradeQuoteVolume ? formatPrice(market.ticker.tradeQuoteVolume, '', false) : '';
        },
        getLowPrice(token) {
            const market = this.marketByPartnerId(token.partner.id);

            return market.ticker && market.ticker.lowPrice ? formatPrice(market.ticker.lowPrice, '', false) : '';
        },
        getHighPrice(token) {
            const market = this.marketByPartnerId(token.partner.id);

            return market.ticker && market.ticker.highPrice ? formatPrice(market.ticker.highPrice, '', false) : '';
        },
        getTradeVolume(token) {
            const market = this.marketByPartnerId(token.partner.id);

            return market.ticker && market.ticker.tradeVolume ? formatPrice(market.ticker.tradeVolume, '', false) : '';
        },
        getMarketFluctuationModifier(token) {
            const market = this.marketByPartnerId(token.partner.id);

            if (market.ticker) {
                if (market.ticker.evolutionPercentage > 0) {
                    return 'row-data__fluctuation--up';
                }

                if (market.ticker.evolutionPercentage < 0) {
                    return 'row-data__fluctuation--down';
                }
            }

            return '';
        },
        areButtonsDisabled(token, type = 'buy') {
            const {
                baseCurrency = {},
                ticker = null,
            } = this.marketByPartnerId(token.partner.id);

            const isActionRestricted = type === 'buy'
                ? this.isTokenPurchaseRestricted(baseCurrency)
                : this.isTokenSaleRestricted(baseCurrency);

            // Sell is unavailable without market
            if (type === 'sell' && !ticker) {
                return true;
            }

            return (!ticker && this.getActiveOffering(token) === null) || isActionRestricted;
        },
        getActiveOffering(token) {
            const voteTokenOfferings = this.voteTokenOfferingsByStatusAndTokenId(['opened', 'published'], token.id);

            return socios.showFto &&
                    voteTokenOfferings.length > 0 &&
                    isWithinInterval(new Date(), {
                        start: new Date(voteTokenOfferings[0].startDate),
                        end: new Date(voteTokenOfferings[0].endDate),
                    })
                ? voteTokenOfferings[0] : null;
        },
        isUnavailable(token) {
            // In case both buttons are disabled or token has an active FTO, don't show the buttons
            return (this.areButtonsDisabled(token) && this.areButtonsDisabled(token, 'sell')) || this.getActiveOffering(token);
        },
        closeExchanges() {
            Object.keys(this.cache.showExchange).forEach((key) => {
                if (this.cache.showExchange[key] === true) {
                    this.cache.showExchange[key] = false;
                }
            });
        },
        toggleExchange(id) {
            this.closeExchanges();
            this.cache.showExchange[id] = !this.cache.showExchange[id];
        },
        updateSearch() {
            if (this.search.length) {
                this.filteredTokens = this.sortedTokens.filter((token) => token.partner.shortName.toLowerCase().includes(this.search.toLowerCase()));

                return;
            }

            this.filteredTokens = this.sortedTokens;
        },
        goToPartnerPage(token) {
            return token.partner.id ? this.$router.push(`/partner/${token.partner.id}`) : '';
        },
        goToBuySell(token, pageType = 'buy') {
            const market = this.marketByPartnerId(token.partner.id);

            return market ? this.$router.push(`/${pageType}-ft/${token.partner.id}`) : '';
        },
        getTooltip(tooltip) {
            return {
                content: tooltip,
                delay: 200,
                placement: 'top-end',
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.trading {
    &__header {
        margin: 0 1rem;
        display: flex;
        align-items: center;

        @include min-width(md) {
            margin: 0 2rem 2rem 2rem;
        }

        &-link {
            margin-left: auto;
            font-size: 1.5rem;
        }
    }

    &__title {
        color: $cyprus-color;
        font-size: 2rem;
        padding-top: 2rem;
        margin-left: 2rem;
    }

    &__search {
        background: $white-color;
        border: 0.1rem solid $french-gray-color;
        border-radius: 0.4rem;
        padding: 1rem 0.9rem;
        min-width: 23rem;
        color: $cyprus-color;
        font-size: 1.5rem;

        &--active {
            border-color: $blue-charcoal-color;
        }
    }

    &__no-results {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        font-size: 1.5rem;
        color: $cyprus-color;
    }

    &__table {
        display: flex;
        flex-direction: column;
        border-radius: 1rem;
        padding: 1.7rem 1rem;
        margin: 0;
        background: none;

        @include min-width(md) {
            margin: 0 2rem;
            background: $white-color;
        }

        &-row {
            display: grid;
            grid-template-columns: 15% 30% 35% 0% 20%;
            padding: 1rem 1.1rem;
            position: relative;
            cursor: pointer;
            background: $white-color;
            margin: 1.2rem 0 1rem 0;

            &:before {
                height: 0;
            }

            @include min-width(md) {
                &:not(:last-child) {
                    &:before {
                        height: 0.3rem;
                        content: "";
                        width: 100%;
                        bottom: -1.2rem;
                        position: absolute;
                        margin-bottom: 0;
                        background: $concrete-color;
                    }
                }
            }

            @include min-width(xl) {
                grid-template-columns: 8% 14% 10% 0% repeat(3, 15%) 13% 10%;
                padding: 1rem 1.1rem;
                border: none;
                margin: 1rem 0;

                &:before {
                    bottom: -1.5rem;
                }
            }

            @include min-width(lg) {
                &:before {
                    bottom: -1.2rem;
                }
            }

            &--show-graphs {
                grid-template-columns: 15% 25% 20% 27% 13%;

                @include min-width(xl) {
                    grid-template-columns: 8% 14% 10% 17% repeat(3, 9%) 13% 11%;
                }
            }

            &:hover {
                background: $hawkes-color;
                border-radius: 1rem;
            }

            &:first-child {
                border: none;
            }

            .row {
                &-overlay {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    border-radius: 1rem;
                    background: rgba($cyprus-color, 0.9);
                    display: flex;
                    justify-content: center;
                    align-items: center;

                    .button {
                        padding: 1.5rem 2.6rem;
                        margin: 0.5rem;

                        &--buy {
                            background: $orange-peel-color;
                        }

                        &--sell {
                            background: $azure-radiance-color;
                        }
                    }
                }

                &-data {
                    font: 600 1.5rem/2rem "Nunito";
                    color: $daintree-color;

                    &__logo {
                        width: 5.5rem;
                        height: 5.5rem;

                        @include max-width(sm) {
                            width: 4rem;
                            height: 4rem;
                        }

                        &--placeholder {
                            border-radius: 50%;
                            background-color: $dusty-gray-color;
                        }
                    }

                    &__token-price {
                        font-size: 1.8rem;
                        background: $athens-gray-color;
                        padding: 0.6rem 1rem;
                        border-radius: 0.6rem;
                        margin-top: 0.2rem;
                        min-width: 7.7rem;
                        text-align: center;

                        @include max-width(sm) {
                            font-size: 1.5rem;
                            min-width: 5.7rem;
                        }
                    }

                    &__fluctuation {
                        @include max-width(sm) {
                            font-size: 1.2rem;
                        }

                        &--up {
                            color: $lima-color;
                        }

                        &--down {
                            color: $monza-color;
                        }
                    }

                    &__fluctuation,
                    &__low-price,
                    &__high-price,
                    &__trade-volume,
                    &__trade-amount {
                        padding-bottom: 0.6rem;
                        text-align: center;
                    }

                    &__title {
                        text-align: right;
                    }

                    &__icon {
                        text-align: right;

                        &-exchange {
                            font-size: 4rem;

                            @include max-width(sm) {
                                font-size: 3rem;
                            }

                            &--disabled {
                                opacity: 0.3;
                            }
                        }

                        &-high {
                            color: $lima-color;
                        }

                        &-low {
                            color: $monza-color;
                        }
                    }

                    &__graph {
                        width: 100%;
                        max-width: 16.7rem;
                    }

                    &--hidden-sm {
                        display: none;

                        @include min-width(xl) {
                            display: inherit;
                        }
                    }
                }
            }
        }
    }
}

.flex {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    flex-direction: column;

    &--horizontal-center {
        align-items: center;
    }

    &--horizontal-left {
        align-items: flex-start;
    }

    &--vertical-center {
        justify-content: center;
    }
}
</style>